import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Trash2, Syringe } from 'lucide-react';
import { memberApi, inputCls } from './memberApi';

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const InjectionsTab = ({ injections, protocols, sites, suggestion, refresh, prefill, clearPrefill }) => {
  const [protocolId, setProtocolId] = useState('');
  const [peptide, setPeptide] = useState('');
  const [dose, setDose] = useState('');
  const [site, setSite] = useState(suggestion || '');
  const [when, setWhen] = useState(toLocalInput(new Date()));
  const [busy, setBusy] = useState(false);
  const activeProtocols = protocols.filter(p => p.active);

  useEffect(() => {
    if (prefill) {
      setProtocolId(prefill.protocol_id);
      setPeptide(prefill.peptide_name);
      setDose(prefill.dose);
      clearPrefill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    if (suggestion && !site) setSite(suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);

  const pickProtocol = (id) => {
    setProtocolId(id);
    const p = activeProtocols.find(x => x.protocol_id === id);
    if (p) {
      setPeptide(p.peptide_name);
      setDose(p.dose);
    } else {
      setPeptide('');
      setDose('');
    }
  };

  const log = async () => {
    if (!peptide.trim() || !dose.trim() || !site) {
      toast.error('Fill in peptide, dose and injection site.');
      return;
    }
    setBusy(true);
    try {
      await memberApi.post('/member/injections', {
        peptide_name: peptide.trim(),
        dose: dose.trim(),
        site,
        injected_at: when ? new Date(when).toISOString() : '',
        protocol_id: protocolId
      });
      toast.success('Injection logged!');
      setWhen(toLocalInput(new Date()));
      setProtocolId('');
      setPeptide('');
      setDose('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log injection.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (inj) => {
    if (!window.confirm('Delete this injection entry?')) return;
    try {
      await memberApi.delete(`/member/injections/${inj.injection_id}`);
      refresh();
    } catch {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div data-testid="injections-tab">
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5 mb-6 space-y-3">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-2">
          <Syringe size={15} /> Log an Injection
        </h3>
        {activeProtocols.length > 0 && (
          <select value={protocolId} onChange={e => pickProtocol(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="injection-protocol-select">
            <option value="">From protocol (optional)...</option>
            {activeProtocols.map(p => <option key={p.protocol_id} value={p.protocol_id}>{p.peptide_name} — {p.dose}</option>)}
          </select>
        )}
        <div className="grid grid-cols-2 gap-3">
          <input value={peptide} onChange={e => setPeptide(e.target.value)} placeholder="Peptide" className={inputCls} data-testid="injection-peptide-input" />
          <input value={dose} onChange={e => setDose(e.target.value)} placeholder="Dose" className={inputCls} data-testid="injection-dose-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select value={site} onChange={e => setSite(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="injection-site-select">
            <option value="">Injection site...</option>
            {sites.map(s => <option key={s} value={s}>{s}{s === suggestion ? ' (suggested)' : ''}</option>)}
          </select>
          <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className={inputCls} data-testid="injection-time-input" />
        </div>
        <button onClick={log} disabled={busy} className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors" data-testid="injection-log-btn">
          {busy ? 'Logging...' : 'Log Injection'}
        </button>
      </div>

      <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-gray-400 mb-3">History</h3>
      {injections.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6" data-testid="no-injections">No injections logged yet.</p>
      ) : (
        <div className="space-y-2">
          {injections.map(inj => (
            <div key={inj.injection_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-sm px-4 py-3 flex items-center justify-between gap-3" data-testid={`injection-${inj.injection_id}`}>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold">{inj.peptide_name} <span className="text-[#D4AF37] font-normal">— {inj.dose}</span></p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {inj.site} · {new Date(inj.injected_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
              <button onClick={() => remove(inj)} className="text-gray-400 hover:text-[#C41E3A] transition-colors flex-shrink-0" data-testid={`injection-delete-${inj.injection_id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
